import { Agent } from "./Agent.js";
import { BotResponse } from "./BotResponse.js";
import { BotResponseType } from "./Enums.js";
import { User } from "./User.js";
/**
 *
 * This class defines an Event from a [[Book]].
 *
 * An event is an object that represents an action (such as posting or deleting a [[Transaction]]) made by an actor (such as a user or a [Bot](https://bkper.com/apps) acting on behalf of a user).
 *
 * @public
 */
export class Event {
    constructor(book, payload) {
        this.book = book;
        this.payload = payload || {};
    }
    /**
     * Gets an immutable copy of the JSON payload for this Event.
     *
     * @returns The wrapped plain json object
     */
    json() {
        return Object.assign({}, this.payload);
    }
    /**
     * Gets the book in which the Event was created.
     *
     * @returns The book in which the Event was created
     */
    getBook() {
        return this.book;
    }
    /**
     * Gets the id of the Event.
     *
     * @returns The id of the Event
     */
    getId() {
        return this.payload.id;
    }
    /**
     * Gets the user who performed the Event.
     *
     * @returns The user who performed the Event
     */
    getUser() {
        return this.payload.user ? new User(this.payload.user, this.book.getConfig()) : undefined;
    }
    /**
     * Gets the Agent who performed the Event.
     *
     * @returns The Agent who performed the Event
     */
    getAgent() {
        return this.payload.agent ? new Agent(this.payload.agent) : undefined;
    }
    /**
     * Gets the date the Event was created.
     *
     * @returns The date the Event was created
     */
    getCreatedAt() {
        return this.payload.createdAt ? new Date(new Number(this.payload.createdAt).valueOf()) : undefined;
    }
    /**
     * Gets the type of the Event.
     *
     * @returns The type of the Event
     */
    getType() {
        return this.payload.type;
    }
    /**
     * Gets the Bot Responses associated to this Event.
     *
     * @returns The Bot Responses associated to this Event
     */
    getBotResponses() {
        if (this.botResponses !== undefined) {
            return this.botResponses;
        }
        let botResponses = [];
        if (this.payload.botResponses) {
            for (const botResponse of this.payload.botResponses) {
                botResponses.push(new BotResponse(this, botResponse));
            }
        }
        this.botResponses = botResponses;
        return this.botResponses;
    }
    /**
     * Checks if this Event has at least one Bot Response of type ERROR.
     *
     * @returns True if this Event has at least one Bot Response of type ERROR
     */
    hasErrorResponse() {
        const botResponses = this.getBotResponses();
        for (const botResponse of botResponses) {
            if (botResponse.getType() === BotResponseType.ERROR) {
                return true;
            }
        }
        return false;
    }
    /** @internal */
    clearCache() {
        this.botResponses = undefined;
    }
}
//# sourceMappingURL=Event.js.map